<template>
  <div class="page-wrapper">
    <!-- Tabs Wrapper -->
    <div class="tabs-wrapper">
      <div class="tabs-header">
        <b-tabs lazy>
          <b-tab :title="$t('globals.inquiries')" active></b-tab>
        </b-tabs>
        <div class="tabs-header-actions">
          <b-button
            variant="success"
            class="create-btn"
            :to="{name: 'newinquiry'}"
            style="background-color: #388e3c;"
          >
            {{ $t("globals.new_inquiry").toUpperCase() }}
          </b-button>
        </div>
      </div>
      <div class="tab-content">
        <div class="tab-content-inner">
            <!-- Search and Filters Section -->
    <div class="filters-section">
      <div class="search-container">
        <label class="search-label">{{ $t("globals.type_to_search") }}</label>
        <b-form-input
          v-model="filter"
          class="search-input"
          :placeholder="$t('globals.search_for') + '...'"
        />
      </div>

      <div class="filter-group">
        <div class="filter-item">
          <label class="filter-label">{{ $t("globals.year") }}</label>
          <b-form-select
            v-model="forYear"
            :options="yearOptions"
            class="filter-select"
          />
        </div>

        <div class="filter-item">
          <label class="filter-label">{{ $t("globals.source") }}</label>
          <b-form-select
            v-model="customerSource"
            :options="customerSourceOptions"
            class="filter-select"
          />
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="table-wrapper">
      <table class="modern-table">
        <thead>
          <tr>
            <th class="checkbox-col">
              <b-form-checkbox v-model="selectAll" @change="toggleSelectAll" />
            </th>
            <th
              v-for="field in fields"
              :key="field.key"
              class="sortable"
              @click="sortByField(field.key)"
            >
              {{ field.label }}
              <span v-if="sortBy === field.key" class="sort-icon">
                {{ sortDesc ? '▼' : '▲' }}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in paginatedItems"
            :key="item.id"
            class="table-row"
            @click="rowClicked(item)"
          >
            <td class="checkbox-col" @click.stop>
              <b-form-checkbox v-model="selectedItems" :value="item.id" />
            </td>
            <td>{{ item.agent ? item.agent.full_name : '-' }}</td>
            <td>
              <router-link
                v-if="item.customer"
                class="customer-link"
                :to="{name: 'customer', params:{ id: item.customer.id }}"
                @click.native.stop
              >
                {{ item.customer.company_name }}
              </router-link>
              <span v-else>-</span>
            </td>
            <td>
              <span class="status-badge" :class="`status-${item.status}`">
                {{ $t('globals.' + item.status) }}
              </span>
            </td>
            <td v-b-tooltip.hover :title="item.created_at">
              {{ formatDate(item.created_at) }}
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="getAllInquiriesLoading" class="loading-state">
        <div class="spinner"></div>
        <p>{{ $t("globals.loading") }}...</p>
      </div>
      <div v-else-if="paginatedItems.length === 0" class="empty-state">
        <p>{{ $t("globals.no_results") }}</p>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination-section">
      <div class="controls-left">
        <span class="summary-label">{{ $t("globals.show") }}</span>
        <b-form-select
          v-model="perPage"
          :options="perPageOptions"
          class="per-page-select"
        />
        <span class="pagination-info">
          {{ paginationText }}
        </span>
      </div>
      <div class="pagination-controls">
        <button
          class="page-btn"
          :disabled="currentPage === 1"
          @click="currentPage--"
        >
          &lt;
        </button>
        <button
          v-for="page in visiblePages"
          :key="page"
          class="page-btn"
          :class="{ active: page === currentPage }"
          @click="currentPage = page"
        >
          {{ page }}
        </button>
        <button
          class="page-btn"
          :disabled="currentPage === totalPages"
          @click="currentPage++"
        >
          &gt;
        </button>
      </div>
    </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  data() {
    var yearOptions = [];
    var currentYear = new Date().getFullYear();

    // Current year until 2018
    for (var i = currentYear; i >= 2018; i--) {
      yearOptions.push({ text: i.toString(), value: i.toString() });
    }

    return {
      forYear: currentYear.toString(),
      yearOptions: yearOptions,
      sortBy: "created_at",
      sortDesc: true,
      filter: null,
      currentPage: 1,
      perPage: 20,
      customerSourceOptions: [ { text: this.$t('globals.all_sources'), value: 'all' } ],
      customerSource: 'all',
      selectAll: false,
      selectedItems: [],
      perPageOptions: [
        { text: "20", value: 20 },
        { text: "40", value: 40 },
        { text: "60", value: 60 }
      ],
      fields: [
        {
          key: "agent.full_name",
          label: this.$t("globals.agent"),
          sortable: true
        },
        {
          key: "customer",
          label: this.$t("globals.customer"),
          sortable: true
        },
        {
          key: "status",
          label: this.$t("globals.status"),
          sortable: true
        },
        {
          key: "created_at",
          label: this.$t("globals.creation_date"),
          sortable: true
        }
      ]
    };
  },
  methods: {
    ...mapActions("inquiries", ["fetchAllInquiries"]),
    ...mapActions("constants", ["fetchActiveEnums"]),
    rowClicked(item) {
      // Don't navigate if user is selecting text
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }
      if (this.getUserBrandPermissions.inquiry_show) {
        this.$router.push({ name: "inquiry", params: { id: item.id } });
      }
    },
    callFetchEnums() {
      this.customerSourceOptions = [{ text: this.$t('globals.all_sources'), value: 'all' }]
      this.fetchActiveEnums({this_ref: this, enums: ["customer_source"]})
        .then((res) => {
          if (res) {
            this.customerSourceOptions = this.customerSourceOptions.concat(res.customer_source);
          }
        })
    },
    sortByField(key) {
      if (this.sortBy === key) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = key;
        this.sortDesc = false;
      }
    },
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedItems = this.paginatedItems.map(item => item.id);
      } else {
        this.selectedItems = [];
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      return dateString.substring(0, 10);
    }
  },
  computed: {
    ...mapGetters("inquiries", ["getAllInquiries", "getAllInquiriesLoading"]),
    ...mapGetters(["getUserBrandPermissions"]),

    filteredItems() {
      let items = [...this.getAllInquiries];

      // Apply text filter
      if (this.filter) {
        const searchTerm = this.filter.toLowerCase();
        items = items.filter(item => {
          const agent = item.agent?.full_name?.toLowerCase() || '';
          const customer = item.customer?.company_name?.toLowerCase() || '';
          const status = item.status?.toLowerCase() || '';
          return agent.includes(searchTerm) ||
                 customer.includes(searchTerm) ||
                 status.includes(searchTerm);
        });
      }

      // Apply sorting
      items.sort((a, b) => {
        let aVal, bVal;

        if (this.sortBy === 'agent.full_name') {
          aVal = a.agent?.full_name || '';
          bVal = b.agent?.full_name || '';
        } else if (this.sortBy === 'customer') {
          aVal = a.customer?.company_name || '';
          bVal = b.customer?.company_name || '';
        } else {
          aVal = a[this.sortBy] || '';
          bVal = b[this.sortBy] || '';
        }

        if (aVal < bVal) return this.sortDesc ? 1 : -1;
        if (aVal > bVal) return this.sortDesc ? -1 : 1;
        return 0;
      });

      return items;
    },

    paginatedItems() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredItems.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredItems.length / this.perPage);
    },

    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        } else if (current >= total - 3) {
          pages.push(1);
          pages.push('...');
          for (let i = total - 4; i <= total; i++) pages.push(i);
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = current - 1; i <= current + 1; i++) pages.push(i);
          pages.push('...');
          pages.push(total);
        }
      }

      return pages;
    },

    paginationText() {
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.filteredItems.length);
      const total = this.filteredItems.length;
      return `${start}-${end} of ${total}`;
    }
  },
  watch: {
    forYear: function(new_date_from) {
      this.fetchAllInquiries({ year: new_date_from, source: this.customerSource });
    },
    customerSource: function(new_customer_source) {
      this.fetchAllInquiries({ year: this.forYear, source: new_customer_source });
    },
    paginatedItems() {
      // Reset selection when page changes
      this.selectAll = false;
      this.selectedItems = [];
    }
  },
  created() {
    if (!this.getUserBrandPermissions.inquiries_index) {
      this.$router.push({ name: "dashboard" });
    }
  },
  mounted() {
    this.callFetchEnums();
    this.fetchAllInquiries({ year: this.forYear, source: this.customerSource });
  }
};
</script>

<style scoped>
/* Minimal page-specific overrides if needed */
</style>
